import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import Seo from "@/components/Seo";
import { api, setToken, fmtErr } from "@/lib/api";

function TargetLogo({ className = "" }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
      <circle cx="32" cy="32" r="30" fill="#fff" stroke="#111" strokeWidth="3" />
      <circle cx="32" cy="32" r="22" fill="#fff" stroke="#111" strokeWidth="3" />
      <circle cx="32" cy="32" r="14" fill="#F04E1E" stroke="#111" strokeWidth="3" />
      <circle cx="32" cy="32" r="5" fill="#111" />
      <path d="M6 6 L34 34" stroke="#4A2E14" strokeWidth="5" strokeLinecap="round" />
      <polygon points="6,6 14,6 6,14" fill="#F04E1E" />
    </svg>
  );
}

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      setToken(r.data.token);
      toast.success("Signed in");
      nav("/admin/dashboard");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo title="Admin | Juwai Morning Teer" description="Admin login" path="/admin" noindex />
      <div className="min-h-screen grid place-items-center px-4 bg-[#FAF7F2]">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-sm rounded-3xl bg-white border border-black/5 p-8 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.20)]"
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-2.5 mb-6">
            <TargetLogo className="w-10 h-10" />
            <div className="font-display font-black leading-none text-xl">
              <div>
                <span className="text-black">JUWAI</span>{" "}
                <span className="text-[#F04E1E]">MORNING TEER</span>
              </div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mt-1">
                Admin Panel
              </div>
            </div>
          </div>

          <div className="w-12 h-12 rounded-2xl bg-[#F04E1E]/10 text-[#F04E1E] grid place-items-center mb-4">
            <Lock className="w-6 h-6" />
          </div>
          <h1 className="font-display text-2xl font-black text-black">Sign in</h1>
          <p className="text-sm text-slate-600 mt-1">
            Enter your admin credentials to manage results.
          </p>

          <label className="block mt-6 text-xs font-bold uppercase tracking-widest text-slate-500">
            Username
          </label>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-xl border border-black/15 bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#F04E1E]/40 focus:border-[#F04E1E]"
            placeholder="admin"
            required
            data-testid="admin-login-email"
          />
          <label className="block mt-4 text-xs font-bold uppercase tracking-widest text-slate-500">
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 w-full rounded-xl border border-black/15 bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#F04E1E]/40 focus:border-[#F04E1E]"
            placeholder="••••••••"
            required
            data-testid="admin-login-password"
          />
          <button
            type="submit"
            disabled={busy}
            className="mt-6 w-full rounded-xl bg-[#F04E1E] text-white py-3 font-bold hover:bg-[#D63E10] transition-colors disabled:opacity-60 shadow-[0_10px_20px_-8px_rgba(240,78,30,0.55)]"
            data-testid="admin-login-submit"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </>
  );
}
